import { useQuery } from '@tanstack/react-query';
import { catalogCardVisibilityQueries } from '@insolech/sdk';
import { supabase } from '@/lib/supabase';

/**
 * useCardVisibilityFilter — single query, three pickers.
 *
 * The web-app new-order catalog pickers (Orthopaedic materials, FITO shoes,
 * Ready-made insoles) each render the ~N cards from `@insolech/catalogs`
 * directly. Admins toggle card-level visibility on /admin/products and the
 * overrides live in `catalog_card_visibility` (sparse, default-to-visible).
 *
 * This hook fans the visibility list out once per picker mount, exposes a
 * loading flag (so the picker can render a skeleton instead of stale all-
 * visible state), and returns a predicate the picker uses inside its filter
 * pipeline. A card is hidden when the visibility row has
 * `web_app_enabled = false` OR `is_draft = true`. Absent rows = visible.
 *
 * NOTE: as of 2026-07 the custom-made SolutionSubtypePicker ALSO calls this
 * hook (mapping each subtype to its CUSTOM-* card code), so an admin can hide a
 * custom material from the web app via catalog_card_visibility.web_app_enabled,
 * exactly like the marketplace pickers. The picker keeps the currently-selected
 * value visible so existing orders still edit correctly.
 */
export function useCardVisibilityFilter() {
  const visibilityQuery = useQuery(
    catalogCardVisibilityQueries.catalogCardVisibilityListQuery(supabase),
  );

  const map = visibilityQuery.data;

  /** True when the card should be HIDDEN on the web-app surface based on
   *  catalog_card_visibility. Absent rows default to visible. */
  const isHidden = (code: string): boolean => {
    if (!map) return false;
    const row = map.find((r) => r.card_code === code);
    if (!row) return false;
    return row.web_app_enabled === false || row.is_draft === true;
  };

  return {
    isLoading: visibilityQuery.isLoading,
    isError: visibilityQuery.isError,
    isHidden,
  };
}
